/**
 * HMAC-SHA256 of `message` under `secretKey`, lowercase hex.
 *
 * Used to sign the identity handed to third parties that hold the same secret
 * and verify by recomputing — there is nothing to decrypt on the other side.
 * Web Crypto is only available in a secure context, so this resolves on https
 * and localhost but not over http on a LAN address.
 */
export async function hmacSha256Hex(
  secretKey: string,
  message: string
): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secretKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(message)
  );

  return Array.from(new Uint8Array(signature))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function getEncryptationAES(secretKey: string, iv: string) {
  async function getKey(): Promise<CryptoKey> {
    // Convert secret string to bytes
    const keyBytes = new TextEncoder().encode(secretKey);

    // Import key for AES-CBC
    return crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-CBC' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  return {
    encrypt: async (value: string): Promise<string> => {
      const key = await getKey();

      const ivBytes = new TextEncoder().encode(iv);
      const valueBytes = new TextEncoder().encode(value);

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-CBC', iv: ivBytes },
        key,
        valueBytes
      );

      // Convert ArrayBuffer → Base64
      return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
    },

    decrypt: async (value: string): Promise<string> => {
      const key = await getKey();

      const ivBytes = new TextEncoder().encode(iv);

      // Base64 → ArrayBuffer
      const encryptedBytes = Uint8Array.from(atob(value), (c) =>
        c.charCodeAt(0)
      );

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-CBC', iv: ivBytes },
        key,
        encryptedBytes
      );

      return new TextDecoder().decode(decrypted);
    },
  };
}
